import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronLeft, ChevronRight, Search, Plus, Check, X, User, Target, Layers, 
  Lock, Minus, BarChart2, Eye, Shield, Users, Clock, Calendar, ChevronDown,
  Trophy, GitCompare, Crown, Activity, Zap, TrendingUp, TrendingDown,
  Swords, Award, Play, CheckCircle, Star, Percent, PieChart, Hash
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 3B: IMPORTS FROM EXTRACTED MODULES
// ═══════════════════════════════════════════════════════════════════════════════

// Constants (Phase 3B Round 1)
import { COLORS, POSITION_COLORS, TEAM_COLORS, INITIAL_ROSTER } from '../constants';

// UI Components (Phase 3B Round 1)
import { LoadingSpinner, ErrorState, SegmentedControl } from './ui';

// Viz Components (Phase 3B Round 1)
import { LabeledEdgeBar, UnitWheel, TeamStrip } from './viz';

// Builder Components
import { FilterBar, PlayerList } from './builder';
import PositionStrip from './builder/PositionStrip';
import OverUnderStrip from './builder/OverUnderStrip';
import GMRosterBuilderV2 from './builder/GMRosterBuilderV2';
import TunnelBuilder from './builder/TunnelBuilder';
import { useRosterBuilder } from '../config/featureFlags';
import { usePoolMembership } from '../hooks';


// Feature Screens (Phase 3B Round 3)
import { LeaderboardScreen } from '../features/leaderboard';
import { StrategyHub, PortfolioReviewScreen } from '../features/strategy';
import { BattleWhatIf } from '../features/compare';
import { MyPerformanceScreenWrapper } from '../features/performance';

// Transformers and Hooks
import { filterPlayers } from '../utils/transformers';
import { 
  useUserEntry, 
  usePoolState,
  useLeaderboard,
  useFieldOverview,
  useCompare,
  useUserPerformance,
  transformStandingToUI,
  buildRosterPayload 
} from "../hooks/useGMStrategy";

console.log('GM Strategy Hub v22 FINAL loaded - slot-based roster builder');

// Debug helper
const DEBUG = true;
const debug = (...args) => DEBUG && console.log('[GM Debug]', ...args);

// ═══════════════════════════════════════════════════════════════════════════════
// MOCK DATA (Single Source of Truth)
// ═══════════════════════════════════════════════════════════════════════════════

const MOCK_POOLS = [
  { id: 1, name: 'NFL Week 14 Main', slots: 7, units: 100, deadline: '2024-12-08T13:00:00', kickoff: '2024-12-08T13:00:00', entrants: 1247, avgUnits: 87, avgPlayers: 6.2, completionPct: 78, status: 'open' },
  { id: 2, name: 'Sunday Night Special', slots: 5, units: 50, deadline: '2024-12-08T20:15:00', kickoff: '2024-12-08T20:20:00', entrants: 534, avgUnits: 45, avgPlayers: 4.8, completionPct: 65, status: 'open' },
  { id: 3, name: 'Week 13 Championship', slots: 7, units: 100, deadline: '2024-12-01T13:00:00', kickoff: '2024-12-01T13:00:00', entrants: 1892, avgUnits: 94, avgPlayers: 7, completionPct: 100, status: 'completed' },
];

const MOCK_PLAYERS = [
  { id: 1, name: 'P. Mahomes', team: 'KC', pos: 'QB', opp: 'LV', avgYds: 285, propYds: 260.5, ydsEdge: 24.5, avgTDs: 2.3, propTDs: 1.5, tdsEdge: 0.8, avgComp: 24.5, propComp: 22.5, compEdge: 2.0, passDefRank: 30, runDefRank: 28, overallRank: 28 },
  { id: 2, name: 'J. Allen', team: 'BUF', pos: 'QB', opp: 'MIA', avgYds: 268, propYds: 255.5, ydsEdge: 12.5, avgTDs: 2.1, propTDs: 1.5, tdsEdge: 0.6, avgComp: 23.2, propComp: 21.5, compEdge: 1.7, passDefRank: 15, runDefRank: 20, overallRank: 12 },
  { id: 3, name: 'L. Jackson', team: 'BAL', pos: 'QB', opp: 'CLE', avgYds: 215, propYds: 225.5, ydsEdge: -10.5, avgTDs: 1.8, propTDs: 1.5, tdsEdge: 0.3, avgComp: 19.5, propComp: 18.5, compEdge: 1.0, passDefRank: 3, runDefRank: 8, overallRank: 5 },
  { id: 4, name: 'J. Hurts', team: 'PHI', pos: 'QB', opp: 'DAL', avgYds: 232, propYds: 220.5, ydsEdge: 11.5, avgTDs: 1.9, propTDs: 1.5, tdsEdge: 0.4, avgComp: 21.0, propComp: 19.5, compEdge: 1.5, passDefRank: 20, runDefRank: 14, overallRank: 18 },
  { id: 5, name: 'D. Henry', team: 'BAL', pos: 'RB', opp: 'CLE', avgYds: 105, propYds: 95.5, ydsEdge: 9.5, avgTDs: 0.9, propTDs: 0.5, tdsEdge: 0.4, avgComp: 2.0, propComp: 1.5, compEdge: 0.5, passDefRank: 8, runDefRank: 5, overallRank: 6 },
  { id: 6, name: 'S. Barkley', team: 'PHI', pos: 'RB', opp: 'DAL', avgYds: 98, propYds: 85.5, ydsEdge: 12.5, avgTDs: 0.8, propTDs: 0.5, tdsEdge: 0.3, avgComp: 3.5, propComp: 2.5, compEdge: 1.0, passDefRank: 14, runDefRank: 10, overallRank: 12 },
  { id: 7, name: 'J. Cook', team: 'BUF', pos: 'RB', opp: 'MIA', avgYds: 82, propYds: 75.5, ydsEdge: 6.5, avgTDs: 0.6, propTDs: 0.5, tdsEdge: 0.1, avgComp: 4.0, propComp: 3.5, compEdge: 0.5, passDefRank: 20, runDefRank: 18, overallRank: 19 },
  { id: 8, name: 'T. Hill', team: 'MIA', pos: 'WR', opp: 'BUF', avgYds: 95, propYds: 85.5, ydsEdge: 9.5, avgTDs: 0.7, propTDs: 0.5, tdsEdge: 0.2, avgComp: 7.0, propComp: 5.5, compEdge: 1.5, passDefRank: 12, runDefRank: 15, overallRank: 13 },
  { id: 9, name: 'C. Lamb', team: 'DAL', pos: 'WR', opp: 'PHI', avgYds: 88, propYds: 80.5, ydsEdge: 7.5, avgTDs: 0.6, propTDs: 0.5, tdsEdge: 0.1, avgComp: 6.5, propComp: 5.5, compEdge: 1.0, passDefRank: 6, runDefRank: 8, overallRank: 7 },
  { id: 10, name: 'T. Kelce', team: 'KC', pos: 'TE', opp: 'LV', avgYds: 68, propYds: 55.5, ydsEdge: 12.5, avgTDs: 0.5, propTDs: 0.5, tdsEdge: 0.0, avgComp: 6.0, propComp: 5.5, compEdge: 0.5, passDefRank: 28, runDefRank: 25, overallRank: 26 },
  { id: 11, name: 'M. Andrews', team: 'BAL', pos: 'TE', opp: 'CLE', avgYds: 55, propYds: 45.5, ydsEdge: 9.5, avgTDs: 0.4, propTDs: 0.5, tdsEdge: -0.1, avgComp: 4.5, propComp: 3.5, compEdge: 1.0, passDefRank: 3, runDefRank: 6, overallRank: 4 },
];

const getPlayerProps = (player) => {
  // Use props from API if available
  if (player.props && player.props.length > 0) {
    return player.props;
  }
  // Fallback to constructed props from player data
  return [
    { id: 1, stat: 'Pass Yards', line: player.propYds, playerAvg: player.avgYds, edge: player.ydsEdge, type: 'passing' },
    { id: 2, stat: 'Pass TDs', line: player.propTDs, playerAvg: player.avgTDs, edge: player.tdsEdge, type: 'passing' },
    { id: 3, stat: 'Completions', line: player.propComp, playerAvg: player.avgComp, edge: player.compEdge, type: 'passing' },
  ];
};

const DEMO_ROSTER = [
  { slot: 1, label: 'QB', player: MOCK_PLAYERS[1], prop: { stat: 'Pass Yards', line: 255.5, type: 'passing' }, side: 'OVER', units: 20 },
  { slot: 2, label: 'RB1', player: MOCK_PLAYERS[6], prop: { stat: 'Rush Yards', line: 75.5, type: 'rushing' }, side: 'OVER', units: 15 },
  { slot: 3, label: 'RB2', player: MOCK_PLAYERS[4], prop: { stat: 'Rush Yards', line: 95.5, type: 'rushing' }, side: 'OVER', units: 15 },
  { slot: 4, label: 'WR1', player: MOCK_PLAYERS[8], prop: { stat: 'Rec Yards', line: 80.5, type: 'receiving' }, side: 'UNDER', units: 15 },
  { slot: 5, label: 'WR2', player: MOCK_PLAYERS[7], prop: { stat: 'Receptions', line: 5.5, type: 'receiving' }, side: 'OVER', units: 10 },
  { slot: 6, label: 'TE', player: MOCK_PLAYERS[9], prop: { stat: 'Rec Yards', line: 55.5, type: 'receiving' }, side: 'UNDER', units: 15 },
  { slot: 7, label: 'FLEX', player: MOCK_PLAYERS[0], prop: { stat: 'Pass TDs', line: 1.5, type: 'passing' }, side: 'OVER', units: 10 },
];

// Entry states: null (not entered), partial, ready, locked, completed
const USER_ENTRIES_INIT = {
  1: null,
  2: { roster: DEMO_ROSTER.slice(0, 4).concat([null, null, null].map((_, i) => INITIAL_ROSTER[i + 4])), totalUnits: 65, status: 'partial' },
  3: { roster: DEMO_ROSTER, totalUnits: 100, score: 798, rank: 127, status: 'completed' },
};

// Mock completed pool history for My Performance
const COMPLETED_POOL_HISTORY = [
  { poolId: 103, poolName: 'Week 13 Championship', week: 13, entrants: 1892, rank: 127, score: 798, unitsEarned: 12, completedAt: '2024-12-01T23:59:00', isWin: false, isTop10: false },
  { poolId: 102, poolName: 'Week 12 Main', week: 12, entrants: 1654, rank: 42, score: 856, unitsEarned: 28, completedAt: '2024-11-24T23:59:00', isWin: false, isTop10: true },
  { poolId: 101, poolName: 'Week 11 Championship', week: 11, entrants: 2103, rank: 8, score: 912, unitsEarned: 45, completedAt: '2024-11-17T23:59:00', isWin: true, isTop10: true },
  { poolId: 100, poolName: 'Week 10 Main', week: 10, entrants: 1432, rank: 156, score: 742, unitsEarned: -8, completedAt: '2024-11-10T23:59:00', isWin: false, isTop10: false },
  { poolId: 99, poolName: 'Week 9 Championship', week: 9, entrants: 1876, rank: 23, score: 878, unitsEarned: 32, completedAt: '2024-11-03T23:59:00', isWin: false, isTop10: true },
  { poolId: 98, poolName: 'Week 8 Main', week: 8, entrants: 1598, rank: 4, score: 934, unitsEarned: 52, completedAt: '2024-10-27T23:59:00', isWin: true, isTop10: true },
  { poolId: 97, poolName: 'Week 7 Championship', week: 7, entrants: 2234, rank: 4, score: 945, unitsEarned: 58, completedAt: '2024-10-20T23:59:00', isWin: true, isTop10: true },
  { poolId: 96, poolName: 'Week 6 Main', week: 6, entrants: 1345, rank: 89, score: 812, unitsEarned: 15, completedAt: '2024-10-13T23:59:00', isWin: false, isTop10: true },
];

// User profile metrics for My Performance
const USER_PROFILE_METRICS = {
  careerWins: 3,
  bestRank: 4,
  bestRankPool: 'Week 7 Championship',
  lifetimeUnits: 234,
  allocationStyle: 'Balanced',
  riskIndex: 'Moderate',
  efficiencyRating: 1.24,
  teamLean: { KC: 28, BAL: 22, BUF: 18, PHI: 15, Other: 17 },
  propLean: { Yards: 45, TDs: 30, Attempts: 25 },
  percentile: 5,
};

const MOCK_STANDINGS = [
  { rank: 1, username: 'GridironGuru', score: 847, weeklyWins: 4, effScore: 1.24, style: 'Concentrated', trend: [78, 82, 85, 88, 92], avgFinish: 2.1, streak: 'W3', roster: DEMO_ROSTER.map((s, i) => ({ ...s, side: i % 2 === 0 ? 'OVER' : 'UNDER', units: [25, 20, 15, 15, 10, 10, 5][i] })) },
  { rank: 2, username: 'PropMaster99', score: 812, weeklyWins: 3, effScore: 1.18, style: 'Balanced', trend: [70, 75, 80, 82, 85], avgFinish: 3.4, streak: 'W1', roster: DEMO_ROSTER.map((s, i) => ({ ...s, side: i % 3 === 0 ? 'UNDER' : 'OVER', units: [15, 15, 15, 15, 15, 15, 10][i] })) },
  { rank: 127, username: 'You', score: 798, weeklyWins: 2, effScore: 1.15, style: 'Broad', trend: [65, 72, 78, 82, 88], isYou: true, avgFinish: 4.2, streak: 'L1' },
  { rank: 128, username: 'ChalkBreaker', score: 776, weeklyWins: 2, effScore: 1.12, style: 'Concentrated', trend: [60, 65, 70, 75, 80], avgFinish: 5.8, streak: 'W2', roster: DEMO_ROSTER.map((s, i) => ({ ...s, side: 'OVER', units: [30, 20, 15, 15, 10, 5, 5][i] })) },
];

const COMPARE_METRICS = {
  ydsEdge: { key: 'ydsEdge', label: 'Yards', short: 'YDS', format: v => `${v >= 0 ? '+' : ''}${v.toFixed(0)}`, maxDefault: 30, type: 'edge' },
  tdsEdge: { key: 'tdsEdge', label: 'TDs', short: 'TD', format: v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}`, maxDefault: 1, type: 'edge' },
  compEdge: { key: 'compEdge', label: 'Comp', short: 'CMP', format: v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}`, maxDefault: 3, type: 'edge' },
  passDefRank: { key: 'passDefRank', label: 'Pass D', short: 'PASS', format: v => `#${v}`, type: 'defense' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

const getPoolState = (pool, entry) => {
  if (pool.status === 'completed' || entry?.status === 'completed') return 'COMPLETED';
  if (entry?.status === 'locked') return 'LOCKED';
  if (!entry) return 'NOT_ENTERED';
  const filledSlots = entry.roster?.filter(s => s.player).length || 0;
  if (filledSlots === pool.slots) return 'READY';
  return 'IN_PROGRESS';
};

const getTimeDisplay = (deadline, kickoff, isLocked) => {
  const now = new Date();
  const target = new Date(isLocked ? kickoff : deadline);
  const diff = target - now;
  if (diff <= 0) return isLocked ? 'Live' : 'Locked';
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return `${hrs}h ${mins}m`;
};

const MiniSparkline = ({ data, width = 50, height = 20, color = COLORS.brand }) => {
  const max = Math.max(...data), min = Math.min(...data), range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`).join(' ');
  return <svg width={width} height={height} className="overflow-visible"><polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={points} /></svg>;
};

const Toast = ({ message, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-slide-down">
      <div className="bg-slate-800 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2">
        <Lock size={16} className="text-amber-400" /><span className="text-sm font-medium">{message}</span>
      </div>
    </div>
  );
};




// ═══════════════════════════════════════════════════════════════════════════════
// NAVIGATION COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const TopContextBar = ({ pool, entry, onBackToPools }) => {
  const poolState = getPoolState(pool, entry);
  const isLocked = poolState === 'LOCKED' || poolState === 'COMPLETED';
  const timeDisplay = poolState === 'COMPLETED' ? 'Final' : getTimeDisplay(pool.deadline, pool.kickoff, isLocked);
  const timeLabel = poolState === 'COMPLETED' ? 'Results' : isLocked ? 'Kickoff' : 'Lock';
  
  return (
    <header className="bg-white shadow-sm px-3 py-2 flex items-center justify-between border-b border-slate-100">
      <button onClick={onBackToPools} className="text-slate-500 text-xs flex items-center gap-1 min-h-[32px] min-w-[60px]">
        <ChevronLeft size={14} />Pools
      </button>
      <div className="text-center flex-1">
        <p className="text-sm font-bold text-slate-900 truncate">{pool?.name}</p>
      </div>
      <div className="flex items-center gap-1 min-w-[70px] justify-end">
        <Clock size={12} className={isLocked ? 'text-slate-400' : 'text-amber-500'} />
        <span className={`text-[11px] font-bold ${isLocked ? 'text-slate-500' : 'text-amber-600'}`}>
          {timeLabel}: {timeDisplay}
        </span>
      </div>
    </header>
  );
};

const BottomNav = ({ currentView, poolState, hasPool, onNavigate, onDisabledTap }) => {
  const items = [
    { id: 'poolSelection', label: 'Pools', icon: Layers },
    { id: 'rosterBuild', label: 'GM Build', icon: Target },
    { id: 'leaderboard', label: 'Scoreboard', icon: Trophy },
    { id: 'strategyHub', label: 'Game Plan', icon: Activity },  // PHASE 3G: Strategy → Game Plan
  ];
  
  const getDisabled = (itemId) => {
    if (itemId === 'poolSelection') return false;
    if (!hasPool) return true;
    if (itemId === 'rosterBuild') return poolState === 'LOCKED' || poolState === 'COMPLETED';
    if (itemId === 'leaderboard') return false;
    if (itemId === 'strategyHub') return poolState !== 'LOCKED' && poolState !== 'COMPLETED';
    return false;
  };
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-1 py-1.5 flex justify-around z-30">
      {items.map(item => {
        const disabled = getDisabled(item.id);
        const isActive = currentView === item.id || 
          (item.id === 'strategyHub' && ['portfolioReview', 'battleWhatIf'].includes(currentView)) ||
          (item.id === 'leaderboard' && currentView === 'rosterCompare');
        
        return (
          <button key={item.id} 
            onClick={() => disabled ? onDisabledTap?.(item.id) : onNavigate(item.id)}
            className={`flex flex-col items-center gap-0.5 py-1.5 px-2 min-w-[72px] min-h-[48px] rounded-lg transition-all
              ${disabled ? 'text-slate-300' : isActive ? 'text-indigo-600 bg-indigo-50' : 'text-slate-500 active:bg-slate-100'}`}>
            <item.icon size={20} />
            <span className="text-[10px] font-semibold">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// POOL SELECTION SCREEN
// ═══════════════════════════════════════════════════════════════════════════════

const PoolSelection = ({ userEntries, onSelectPool, onMyPerformance }) => {
  const activePools = MOCK_POOLS.filter(p => p.status !== 'completed');
  
  return (
    <div className="min-h-screen bg-slate-100 pb-6">
      <header className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-bold">North Star</h1>
            <p className="text-slate-400 text-[11px]">Sports Intelligence OS</p>
          </div>
          {/* My Performance button per work order Section 6 */}
          <button onClick={onMyPerformance} 
            className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center active:scale-95 transition-all">
            <BarChart2 size={20} />
          </button>
        </div>
        <button className="flex items-center gap-2 bg-slate-700 rounded-lg px-4 py-2.5 w-full min-h-[44px]">
          <Calendar size={16} className="text-slate-400" />
          <span className="flex-1 text-left font-semibold text-sm">Week 14</span>
          <ChevronDown size={16} className="text-slate-400" />
        </button>
      </header>
      
      <main className="p-3 space-y-3">
        <h2 className="text-[11px] font-semibold text-slate-500 uppercase px-1">Active Pools</h2>
        
        {activePools.map(pool => {
          const entry = userEntries[pool.id];
          const state = getPoolState(pool, entry);
          const filledSlots = entry?.roster?.filter(s => s.player).length || 0;
          const usedUnits = entry?.totalUnits || 0;
          const timeDisplay = getTimeDisplay(pool.deadline, pool.kickoff, state === 'LOCKED');
          
          let ctaLabel, ctaIcon, ctaStyle, subtext;
          switch (state) {
            case 'NOT_ENTERED':
              ctaLabel = 'Build Roster'; ctaIcon = Plus; ctaStyle = 'bg-indigo-600 text-white';
              subtext = `${pool.slots} slots • $${pool.units} • Lock in ${timeDisplay}`; break;
            case 'IN_PROGRESS':
              ctaLabel = 'Resume Roster'; ctaIcon = Target; ctaStyle = 'bg-indigo-600 text-white';
              subtext = `${filledSlots} of ${pool.slots} slots • $${usedUnits}/$${pool.units} • Lock in ${timeDisplay}`; break;
            case 'READY':
              ctaLabel = 'Review & Lock'; ctaIcon = CheckCircle; ctaStyle = 'bg-amber-500 text-white';
              subtext = `${filledSlots} of ${pool.slots} slots • $${usedUnits}/$${pool.units} • Lock in ${timeDisplay}`; break;
            case 'LOCKED':
              ctaLabel = 'View Live Board'; ctaIcon = Play; ctaStyle = 'bg-slate-700 text-white';
              subtext = `Pool Locked • Games in ${timeDisplay}`; break;
            default:
              ctaLabel = 'View Results'; ctaIcon = Trophy; ctaStyle = 'bg-slate-600 text-white';
              subtext = `Final rank: #${entry?.rank || '—'}`;
          }
          
          const statusBadge = {
            'NOT_ENTERED': { label: 'Not Entered', style: 'bg-slate-200 text-slate-600' },
            'IN_PROGRESS': { label: 'In Progress', style: 'bg-amber-100 text-amber-700' },
            'READY': { label: 'Ready', style: 'bg-emerald-100 text-emerald-700' },
            'LOCKED': { label: 'Locked', style: 'bg-indigo-100 text-indigo-700' },
            'COMPLETED': { label: 'Completed', style: 'bg-slate-100 text-slate-600' },
          }[state];
          
          const CtaIcon = ctaIcon;
          
          return (
            <div key={pool.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-slate-900 text-sm">{pool.name}</h3>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusBadge.style}`}>{statusBadge.label}</span>
                </div>
                <div className="flex items-center gap-4 text-[11px] text-slate-500 mb-3">
                  <span className="flex items-center gap-1"><Users size={11} /> {pool.entrants.toLocaleString()}</span>
                  <span>Avg: ${pool.avgUnits}</span>
                  <span>{pool.completionPct}% complete</span>
                </div>
                <button onClick={() => onSelectPool(pool, state)}
                  className={`w-full py-3 min-h-[48px] font-semibold rounded-lg text-sm flex items-center justify-center gap-2 ${ctaStyle} active:scale-[0.98] transition-all`}>
                  <CtaIcon size={18} />{ctaLabel}
                </button>
                <p className="text-[11px] text-slate-400 text-center mt-2">{subtext}</p>
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════════════

const App = ({ poolId: initialPoolId } = {}) => {
  console.log('[GM App] Mounted with poolId:', initialPoolId);
  
  // Phase 2F: LIVE MODE ONLY - no demo fallback
  // If no poolId, show error - this forces clean debugging
  if (!initialPoolId) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <X size={24} className="text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-2">Pool ID Required</h2>
          <p className="text-slate-600 text-sm mb-2">
            This page requires a pool ID from the route.
          </p>
          <p className="text-xs text-slate-400 font-mono bg-slate-50 p-2 rounded">
            Expected: /pools/:poolId/gm<br/>
            Received poolId: {String(initialPoolId)}
          </p>
        </div>
      </div>
    );
  }
  
  const CURRENT_USER_ID = localStorage.getItem('current_user_id') || 'guest';
  
  // Fetch slate (players/props) from API

  
  // Fetch user entry from API
  const { 
    entry, 
    state: entryState,
    loading: entryLoading,
    error: entryError,
    saveEntry,
    lockEntry 
  } = useUserEntry(initialPoolId, CURRENT_USER_ID);
  
  
  
  // Local UI state
  // Check for view query param (e.g., ?view=strategyHub from Game Plan button)
  const getInitialView = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') || 'rosterBuild';
  };
  const [view, setView] = useState(getInitialView);
  const [toastMessage, setToastMessage] = useState(null);
  const [battleOpponent, setBattleOpponent] = useState(null);
  
  // Get user's balance for capital deployed calculation
  const { membership } = usePoolMembership(initialPoolId);
  const availableBalance = membership?.current_balance || 1000;

  // Derived state for navigation
  const poolState = entryState;
  const isLocked = entryState === 'LOCKED';
  const builderType = useRosterBuilder();
  
  // Handle locking roster through the hooks
  const handleLockRoster = async (roster, totalUnits) => {
    const rosterPayload = buildRosterPayload(roster);
    
    try {
      const saveResult = await saveEntry(rosterPayload);
      if (!saveResult?.success) {
        setToastMessage(saveResult?.errors?.map(e => typeof e === 'object' ? (e.message || JSON.stringify(e)) : e).join(', ') || 'Failed to save roster');
        return;
      }
      
      const lockResult = await lockEntry();
      if (lockResult?.success) {
        setToastMessage('Roster locked successfully');  // PHASE 3G: Portfolio → Roster
        setView('leaderboard');
      } else {
        setToastMessage(lockResult?.errors?.map(e => typeof e === 'object' ? (e.message || JSON.stringify(e)) : e).join(', ') || 'Failed to lock roster');  // PHASE 3G
      }
    } catch (err) {
      console.error('API error:', err);
      setToastMessage('An error occurred');
    }
  };
  
  const handleBack = () => window.history.back();

  // LOADING STATE - entry only (V2 handles slate loading internally)
  if (entryLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Navigation handlers
  const handleNavigate = (viewId) => {
    if (viewId === 'poolSelection') {
      // Navigate back to pools list
      window.location.href = '/pools';
      return;
    }
    setView(viewId);
  };
  
  const handleDisabledTap = (itemId) => {
    if (itemId === 'strategyHub') {
      setToastMessage('Lock your roster to access Game Plan');  // PHASE 3G: Strategy Hub → Game Plan
    } else if (itemId === 'rosterBuild' && isLocked) {
      setToastMessage('Roster is locked');
    }
  };
  
  const handleBattleWhatIf = (opponent) => {
    setBattleOpponent(opponent);
    setView('battleWhatIf');
  };

  return (
    <div className="font-[Inter] antialiased">
      <div className="max-w-md mx-auto bg-slate-100 min-h-screen pb-16">
        {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}
        
        {/* ROSTER BUILD VIEW - BUCKET 4: Feature flag routing */}
        {view === 'rosterBuild' && (
          builderType === 'tunnel' ? (
            <TunnelBuilder
              pool={{ id: initialPoolId }}
              initialRoster={(entry?.roster?.length > 0) ? entry.roster : null}
              isLocked={isLocked}
              saveEntry={saveEntry}
              lockEntry={lockEntry}
              completeEntry={async () => {
                const response = await fetch(`/api/v1/pools/${initialPoolId}/entry/complete`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-User-ID': localStorage.getItem('userId') || 'test_user_001'
                  }
                });
                return response.json();
              }}
              onBack={handleBack}
              onSuccess={() => setView('leaderboard')}
              showToast={setToastMessage}
            />
          ) : (
            <GMRosterBuilderV2 
              pool={{ id: initialPoolId }}
              initialRoster={(entry?.roster?.length > 0) ? entry.roster : null}
              isLocked={isLocked}
              saveEntry={saveEntry}
              lockEntry={lockEntry}
              onBack={handleBack}
              onSuccess={() => setView('leaderboard')}
              showToast={setToastMessage}
            />
          )
        )}
        
        {/* LEADERBOARD VIEW - Real component, not placeholder */}
        {view === 'leaderboard' && (
          <LeaderboardScreen
            pool={{ id: initialPoolId }}
            entry={entry}
            poolState={poolState}
            onBattleWhatIf={handleBattleWhatIf}
            onShowToast={setToastMessage}
          />
        )}
        
        {/* GAME PLAN VIEW - PHASE 3G renamed from Strategy Hub */}
        {view === 'strategyHub' && (
          <StrategyHub
            pool={{ id: initialPoolId }}
            onPortfolio={() => setView('portfolioReview')}
            onBattleWhatIf={() => { setToastMessage('Tap a player to compare head-to-head'); setView('leaderboard'); }}
          />
        )}
        
        {/* ROSTER REVIEW VIEW - PHASE 3G renamed from Portfolio Review */}
        {view === 'portfolioReview' && (
          <PortfolioReviewScreen
            pool={{ id: initialPoolId }}
            entry={entry}
            onBack={() => setView('strategyHub')}
          />
        )}
        
        {/* HEAD-TO-HEAD VIEW - PHASE 3G renamed from Battle What-If */}
        {view === 'battleWhatIf' && (
          <BattleWhatIf
            pool={{ id: initialPoolId, pool_id: initialPoolId }}
            entry={entry}
            opponent={battleOpponent}
            onBack={() => setView('leaderboard')}
          />
        )}
        
        {/* BOTTOM NAV - Always show for navigation */}
        <BottomNav
          currentView={view}
          poolState={poolState}
          hasPool={true}
          onNavigate={handleNavigate}
          onDisabledTap={handleDisabledTap}
        />
      </div>
      
      <style>{`
        @keyframes scale-in { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-scale-in { animation: scale-in 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default App;
